import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Mail, Lock, AlertCircle } from 'lucide-react';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#2563eb] via-[#1d4ed8] to-[#16a34a] items-center justify-center p-12">
                <div className="max-w-md">
                    <div className="flex items-center gap-3 mb-8">
                        <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-14 w-14 object-contain bg-white rounded-2xl p-1.5 shadow-lg" />
                        <span className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            DIGI-LOANS
                        </span>
                    </div>
                    <h1 className="text-4xl font-black text-white mb-4 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Complete HR Management Solution
                    </h1>
                    <p className="text-blue-100 text-lg leading-relaxed">
                        Streamline your workforce with our all-in-one HR platform. 
                        Manage employees, track attendance with GPS, handle payroll, projects, tasks, assets, and field force - all in one place.
                    </p>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
                        <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-10 w-10 object-contain" />
                        <span className="text-2xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            DIGI-LOANS
                        </span>
                    </div>

                    <div className="bg-white border border-slate-200 p-8">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Sign In
                            </h2>
                            <p className="text-slate-500 mt-2">
                                Enter your credentials to access the dashboard
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 p-4 mb-6 flex items-start gap-3" data-testid="login-error">
                                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-red-800 text-sm">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="space-y-5">
                                <div>
                                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                                        Email or Employee ID
                                    </Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="text"
                                            autoComplete="username"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@company.com  or  EMP1234ABCD"
                                            className="pl-10 h-12 rounded-none border-slate-300 focus:border-brand focus:ring-brand"
                                            required
                                            data-testid="login-email-input"
                                        />
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1.5">Use your registered email or the Employee ID printed on your visiting card</p>
                                </div>

                                <div>
                                    <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                                        Password
                                    </Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <Input
                                            id="password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            className="pl-10 h-12 rounded-none border-slate-300 focus:border-brand focus:ring-brand"
                                            required
                                            data-testid="login-password-input"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-brand hover:bg-brand text-white font-medium rounded-none transition-colors"
                                    disabled={isLoading}
                                    data-testid="login-submit-btn"
                                >
                                    {isLoading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
